import { Injectable } from "@angular/core";
import { Platform, Slides } from "ionic-angular";
import { FormInlined } from "../../business-objects/form/form.model";
import { FormStateService } from "./form-state.service";
import { BaseController } from "../../../core/controllers/base.controller";
import { zip } from "rxjs/observable/zip";
import { GroupInlined } from "../../business-objects/group/group.model";
import { FormEventsService } from "./form-events.service";
import { Subscription } from "rxjs/Subscription";

@Injectable()
export class FormSlidesController extends BaseController {

    protected form: FormInlined;
    protected slides: Slides;
    protected _lockSlides: boolean;

    protected slideFromIndex: number = 0;
    protected slideToIndex: number = 0;
    protected displayingGroups: Array<GroupInlined> = [];
    protected targetGroupIndex: number = 0;
    protected activeGroupIndex: number = 0;
    protected slideTransitionSpeed = 300;
    protected slideCount: number = 0;
    protected ionSlideDidChangeSubscription: Subscription;

    constructor(
        protected platformService: Platform,
        protected stateService: FormStateService,
        protected eventsService: FormEventsService
    ) {
        super();

        zip(
            this.stateService.activeGroupIndex$,
            this.stateService.targetGroupIndex$,
            this.stateService.slideToIndex$,
            this.stateService.slideFromIndex$,
            this.stateService.displayingGroups$,
            this.stateService.slideCount$
        )
            .takeUntil(this.ngUnsubscribe)
            .subscribe(args => {
                let activeGroupIndex: number;
                let targetGroupIndex: number;
                let slideToIndex: number;
                let slideFromIndex: number;
                let displayingGroups: Array<GroupInlined>;
                let slideCount: number;

                [activeGroupIndex, targetGroupIndex, slideToIndex, slideFromIndex, displayingGroups, slideCount] = args;

                this.activeGroupIndex = activeGroupIndex;
                this.targetGroupIndex = targetGroupIndex;
                this.slideCount = slideCount;

                const debouce = 10;
                // if we are about to change
                if (activeGroupIndex !== targetGroupIndex) {
                    this.slideTo(slideToIndex, this.slideTransitionSpeed, true, debouce + 1);
                } else {
                    // the change has finished
                    this.slideFromIndex = slideFromIndex;
                    this.slideToIndex = slideToIndex;
                    this.displayingGroups = displayingGroups;
                    // console.log("groups", displayingGroups.map(group => group.title));
                    // this.slides.update(debouce);
                    this.slideTo(slideFromIndex, 0, false);
                }
            });
    }

    setForm(form: FormInlined, lockSlides: boolean = false): void {
        this.form = form;
        this._lockSlides = lockSlides;

        if (this.form.groups.length === 1 || lockSlides) {
            this.lockSlides();
        }
    }

    setSlides(slides: Slides): void {

        if (slides === this.slides) {
            return;
        }

        this.slides = slides;

        this.unregisterSlidesEvents(slides);

        if (this.ionSlideDidChangeSubscription) {
            this.ionSlideDidChangeSubscription.unsubscribe();
        }

        this.ionSlideDidChangeSubscription = slides.ionSlideDidChange
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => {
                this.stateService.setActiveGroupIndex(this.targetGroupIndex);
                this.eventsService.ionSlideDidChange.next();
            });

        this.slideTo(this.slideFromIndex, 0, false);
    }

    protected slideTo(index, speed = 500, runCallBacks = true, delay?): void {

        if (delay === undefined) {
            this._slideTo(index, speed, runCallBacks);
        } else {
            setTimeout(() => {
                this._slideTo(index, speed, runCallBacks);
            }, delay);
        }
    }

    protected _slideTo(index: number, speed: number = 500, runCallbacks: boolean = true): void {

        if (!this.slides || index === this.slides.getActiveIndex()) {
            return;
        }

        this.unlockSlides();

        try {
            this.slides.update();
            this.slides.slideTo(index, speed, runCallbacks);
        } catch (e) {
            // noop
        }

        this.lockSlides();
    }

    /**
     * This function is a bit of a hack to get Slides to unregister all the mouse and pointer events it creates. The
     * reason we do this is because Slides basically takes over all the click and pointer events in such a way that a
     * user cannot click/drag/select text inside input fields - which is bad for UX.
     */
    protected unregisterSlidesEvents(slides: Slides): void {

        if (this.platformService.is("mobile")) {
            return;
        }

        setTimeout(() => {
            try {

                if ((slides as any)._unregs.length === 0) {
                    return;
                }

                (slides as any)._unregs[0]();
                delete (this.slides as any)._unregs[0];
                (slides as any)._unregs.length = 0;
                (slides as any)._unregs = [];
            } catch (e) {
                console.error(e);
            }
        }, 1000);
    }

    /**
     * @return {number}
     */
    protected calcPrevIndex(): number {

        if (this.activeGroupIndex === 0) {
            return 0;
        }

        return this.activeGroupIndex - 1;
    }

    /**
     * @return {number}
     */
    protected calcNextIndex(): number {

        if (this.activeGroupIndex === this.slideCount - 1) {
            return this.slideCount - 1;
        }

        return this.activeGroupIndex + 1;
    }

    jumpToSlide(index: number): void {

        if (this.slideCount === 0) {
            return;
        }

        this.stateService.setTargetAndActiveGroupIndexes(index, index);
    }

    nextSlide(): void {

        if (this.slideCount === 0) {
            return;
        }

        const index = this.calcNextIndex();

        this.stateService.setTargetGroupIndex(index);
    }

    prevSlide(): void {

        if (this.slideCount === 0) {
            return;
        }

        const index = this.calcPrevIndex();

        this.stateService.setTargetGroupIndex(index);
    }

    lockSlides(): void {
        if (!this.slides) return;
        this.slides.lockSwipes(true);
    }

    unlockSlides(): void {
        if (!this.slides) return;
        this.slides.lockSwipes(false);
    }

}



// WEBPACK FOOTER //
// ./src/vas/pages/form/form-slides.controller.ts